import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import {
  accountBodyClassName,
  accountCardClassName,
  accountHeaderClassName,
} from "./accountFormLayout";
import { Card } from "packages/dsaui/src/components";
import { cn } from "packages/dsaui/src/utils";

const SkeletonBlock = ({ className }: { className?: string }) => (
  <div aria-hidden className={cn("rounded-md bg-gray-200", className)} />
);

const AccountFormCardSkeleton = ({
  "aria-label": ariaLabel,
  showNameFields = false,
}: {
  "aria-label": string;
  showNameFields?: boolean;
}) => (
  <Card aria-busy="true" aria-label={ariaLabel} className={accountCardClassName}>
    <div className={accountHeaderClassName}>
      <SkeletonBlock className="h-6 w-40" />
    </div>
    <div className={accountBodyClassName}>
      {showNameFields ? (
        <div className="flex flex-col gap-6 lg:flex-row lg:gap-6">
          <div className="min-w-0 flex-1 space-y-2">
            <SkeletonBlock className="h-4 w-24" />
            <SkeletonBlock className="h-10 w-full" />
          </div>
          <div className="min-w-0 flex-1 space-y-2">
            <SkeletonBlock className="h-4 w-24" />
            <SkeletonBlock className="h-10 w-full" />
          </div>
        </div>
      ) : null}
      <div className="rounded-md bg-gray-100 p-4">
        <div className="space-y-2">
          <SkeletonBlock className="h-4 w-full" />
          <SkeletonBlock className="h-4 w-5/6" />
        </div>
      </div>
      {!showNameFields ? (
        <div className="space-y-2">
          <SkeletonBlock className="h-4 w-16" />
          <SkeletonBlock className="h-10 w-full" />
        </div>
      ) : null}
      <SkeletonBlock className="h-11 w-full" />
    </div>
  </Card>
);

export const AccountFormSkeleton = (): JSX.Element => {
  const { t } = useTranslation();

  return (
    <div aria-busy="true" className="flex animate-pulse flex-col gap-8">
      <AccountFormCardSkeleton
        aria-label={t("dashboard.loading.card")}
        showNameFields
      />
      <AccountFormCardSkeleton aria-label={t("dashboard.loading.card")} />
    </div>
  );
};

export default AccountFormSkeleton;
